import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import {
  DatePicker,
  DesktopTimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Close, Delete, Edit } from "@mui/icons-material";
import SelectManufacturingOrder from "./SelectManufacturingOrder";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import SelectEventOrder from "./SelectEventOrder";
import { AuthContext } from "../../../../context/AuthContext";
import JobEvent from "./JobEvent";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

const ScheduleJobEventsDialog = ({
  dialogData,
  handleClose,
  handleSubmit,
  errorMessage,
}) => {
  const theme = useTheme();
  const { authToken, ezorder, decoded } = useContext(AuthContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const validationSchema = Yup.object({
    eventEntity: Yup.mixed()
      .oneOf(["IND", "RFL"], "Entity required")
      .required("Entity required"),
    date: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid date",
      (value) => dayjs.isDayjs(value) && value.isValid()
    ),
    startDate: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid start time",
      (value) => dayjs.isDayjs(value) && value.isValid()
    ),
    endDate: Yup.mixed().test(
      "is-valid-date",
      "Please select a valid stop time",
      (value) => dayjs.isDayjs(value) && value.isValid()
    ),
    order: Yup.object()
      .nullable()
      .when("eventEntity", {
        is: "IND", // alternatively: (val) => val == true
        then: Yup.object()
          .nullable()
          .shape({
            id: Yup.number().required("Select an Industry Job"),
          })
          .required("Select an Industry Job"),
        otherwise: Yup.object().nullable(),
      }),
    manufacturingOrderId: Yup.number("Select a mfg")
      .nullable()
      .when("eventEntity", {
        is: "RFL", // alternatively: (val) => val == true
        then: Yup.number().nullable().required("Select an RFL Job"),
        otherwise: Yup.number().nullable(),
      }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: true,
    initialValues: {
      date: null,
      order: null,
      manufacturingOrderId: null,
      rflTasks: [],
      subPhases: [],
      calendarEvents: [],
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      // Submit
      handleSubmit(values, formik);
    },
  });
  if (formik && formik.values)
    return (
      <Dialog
        open={dialogData.open}
        onClose={() => {
          handleClose();
          formik.resetForm();
        }}
        maxWidth="lg"
        fullWidth={true}
      >
        <DialogTitle
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Typography
            sx={{
              flex: 1,
              fontFamily: "inherit",
              fontWeight: "inherit",
              fontSize: "inherit",
            }}
          >
            {dialogData.isRescheduling
              ? "Re-schedule Unfinished Tasks"
              : "Schedule a Job"}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-start",
            }}
          >
            <IconButton
              onClick={() => {
                handleClose();
                formik.resetForm();
              }}
              sx={{}}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        {dialogData.open && (
          <DialogContent>
            <Box>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "row",
                  paddingTop: "8px",
                }}
              >
                <Box sx={{ flex: 1, marginRight: "8px" }}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      sx={{}}
                      format="MM/DD/YYYY"
                      label="Date"
                      value={formik.values.date}
                      onChange={(newValue) => {
                        let date = dayjs(newValue)
                          .tz(DEFAULT_TIMEZONE)
                          .startOf("day");
                        let startDate = date.startOf("day").add(7, "hours");
                        let endDate = date.startOf("day").add(8, "hours");

                        formik.setValues((prev) => ({
                          ...prev,
                          date,
                          startDate,
                          endDate,
                          eventEntity: "",
                          order: null,
                          manufacturingOrderId: null,
                          calendarEvents: [], // Clear Out Calendar Events
                          rflTasks: [],
                          subPhases: [],
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField
                          size="small"
                          fullWidth
                          sx={{}}
                          onBlur={formik.handleBlur}
                          error={
                            formik.touched.date && Boolean(formik.errors.date)
                          }
                          helperText={
                            formik.errors.date && `${formik.errors.date}`
                          }
                          onFocus={(e) => {
                            e.target.select();
                          }}
                          {...params}
                        />
                      )}
                    />
                  </LocalizationProvider>
                </Box>
                <Box sx={{ flex: 1, marginRight: "8px" }}>
                  <TextField
                    size="small"
                    select
                    variant="outlined"
                    label="Schedule Job For"
                    id="eventEntity"
                    name="eventEntity"
                    fullWidth
                    value={formik.values.eventEntity}
                    onChange={(e) => {
                      formik.setValues({
                        ...formik.values,
                        eventEntity: e.target.value,
                        order: null,
                        manufacturingOrderId: null,
                        calendarEvents: [], // Clear Out Calendar Events
                        rflTasks: [],
                        subPhases: [],
                      });
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.eventEntity &&
                      Boolean(formik.errors.eventEntity)
                    }
                    helperText={
                      formik.errors.eventEntity &&
                      `${formik.errors.eventEntity}`
                    }
                  >
                    <MenuItem value={"IND"}>Industry</MenuItem>
                    <MenuItem value={"RFL"}>RFL</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ flex: 1 }}>
                  {/* Jobs - select */}
                  {formik.values.eventEntity == "IND" && (
                    <SelectEventOrder
                      size="small"
                      value={formik.values.order}
                      onUpdate={(order) => {
                        formik.setValues((prev) => ({
                          ...prev,
                          order,
                          calendarEvents: [
                            {
                              date: prev.date,
                              startDate: prev.startDate,
                              endDate: prev.endDate,
                              eventEntity: prev.eventEntity,
                              order,
                              rflTasks: [],
                              subPhases: [],
                            },
                          ], // Default 1 event
                        }));
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.order && Boolean(formik.errors.order)
                      }
                      helperText={
                        formik.errors.order && `${formik.errors.order}`
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  )}
                  {formik.values.eventEntity == "RFL" && (
                    <SelectManufacturingOrder
                      value={
                        formik.values.manufacturingOrderId
                          ? { id: formik.values.manufacturingOrderId }
                          : null
                      }
                      onUpdate={(mfgOrder) => {
                        // console.log("onUpdate", mfgOrder);
                        // formik.setFieldValue("manufacturingOrderId", mfgOrder.id);
                        formik.setValues((prev) => ({
                          ...prev,
                          manufacturingOrderId: mfgOrder.id,

                          calendarEvents: [
                            {
                              date: prev.date,
                              startDate: prev.startDate,
                              endDate: prev.endDate,
                              eventEntity: prev.eventEntity,
                              manufacturingOrderId: mfgOrder.id,
                              rflTasks: [],
                              subPhases: [],
                            },
                          ], // Default 1 event
                        }));
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.manufacturingOrderId &&
                        Boolean(formik.errors.manufacturingOrderId)
                      }
                      helperText={
                        formik.errors.manufacturingOrderId &&
                        `${formik.errors.manufacturingOrderId}`
                      }
                      onFocus={(e) => {
                        e.target.select();
                      }}
                    />
                  )}
                </Box>
              </Box>

              {formik.values.calendarEvents &&
                formik.values.calendarEvents.map((calendarEvent, index) => {
                  return (
                    <Card key={index} sx={{ marginTop: "8px" }}>
                      <JobEvent
                        key={index}
                        calendarEvent={calendarEvent}
                        handleRemove={() => {
                          let calendarEventsCopy = [
                            ...formik.values.calendarEvents,
                          ];
                          calendarEventsCopy.splice(index, 1);
                          formik.setFieldValue(
                            "calendarEvents",
                            calendarEventsCopy
                          );
                        }}
                        handleChange={(c) => {
                          console.log("handleChange", c);
                          let calendarEventsCopy = [
                            ...formik.values.calendarEvents,
                          ];
                          calendarEventsCopy[index] = c;
                          formik.setFieldValue(
                            "calendarEvents",
                            calendarEventsCopy
                          );
                        }}
                      />
                    </Card>
                  );
                })}
              {/* <Box>
                <Button
                  onClick={() => {
                    let calendarEventsCopy = [...formik.values.calendarEvents];
                    calendarEventsCopy.push({
                      date: formik.values.date,
                      startDate: formik.values.startDate,
                      endDate: formik.values.endDate,
                      eventEntity: formik.values.eventEntity,
                      order: formik.values.order,
                      manufacturingOrderId: formik.values.manufacturingOrderId,
                    });
                    formik.setFieldValue("calendarEvents", calendarEventsCopy);
                  }}
                >
                  Schedule Another
                </Button>
              </Box> */}

              {errorMessage ? (
                <Typography sx={{ color: "#d32f2f", marginTop: "5px" }}>
                  {errorMessage}
                </Typography>
              ) : (
                <></>
              )}
            </Box>
          </DialogContent>
        )}

        <DialogActions>
          <Button
            onClick={() => {
              formik.handleSubmit();
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    );
};

export default ScheduleJobEventsDialog;
